import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';

import { cn } from '~/utils/cn';

import { Button } from './ui/button';

import type { HeroIcon } from '~/types';

type InfoCardProps = {
  Icon: HeroIcon;
  actions?: boolean;
  children: React.ReactNode;
  className?: string;
  id: string;
  title: string;
  variant?: 'Default' | 'Destructive' | 'Success' | 'Important';
};

const variantStyles = {
  Default: 'bg-accent-blue-400/20 border-accent-blue-600 text-accent-blue-800',
  Destructive: 'bg-accent-red-50 border-red-300 ',
  Success:
    'bg-accent-green-50/40  border-accent-green-300 text-accent-green-500',
  Important: 'bg-yellow-100/30  border-yellow-300/50 text-yellow-800',
};

const animationVariants = {
  hidden: {
    opacity: 0,
    height: '0px',
    marginBottom: '0px',
    y: -150,
    scale: 0.95,
  },
  visible: { opacity: 1, y: 0, height: 'auto', marginBottom: '24px', scale: 1 },
  exit: {
    opacity: 0,
    height: '0px',
    marginBottom: '0px',
    scale: 0.95,
  },
};

const InfoCard: React.FC<InfoCardProps> = ({
  id,
  variant = 'Default',
  Icon,
  title,
  children,
  className,
  actions = true,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [remindLater, setRemindLater] = useState(false);

  useEffect(() => {
    const storedDismissalState = localStorage.getItem(`InfoCard-${id}`);

    setIsVisible(storedDismissalState !== 'dismissed');
  }, [id]);

  const handleDismiss = () => {
    localStorage.setItem(`InfoCard-${id}`, 'dismissed');
    setIsVisible(false);
  };

  const handleRemindLater = () => {
    setRemindLater(true);
  };

  return (
    <AnimatePresence mode="wait">
      {isVisible && !remindLater && (
        <motion.div
          key="infoCard"
          layout
          className={cn(
            'shadow-drop relative w-fit max-w-[600px] cursor-default items-start rounded-2xl border backdrop-blur-[1px]',
            variantStyles[variant],
            className,
          )}
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={animationVariants}
          transition={{ duration: 0.6, ease: [0.25, 1, 0.5, 1] }}
        >
          <div className="flex w-full p-4 pr-6">
            <div className="mt-0.5 mr-2.5">
              {Icon && <Icon className="size-4" />}
            </div>

            <div className="flex flex-col gap-5">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.05 }}
                className="flex flex-col gap-0.5"
              >
                <p className="mb-1.5 text-sm font-semibold tracking-tight">
                  {title}
                </p>
                {children}
              </motion.div>

              {actions && (
                <motion.div
                  className="flex flex-row gap-2"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <Button
                    variant="default"
                    size="sm"
                    className="w-fit text-xs"
                    onClick={handleDismiss}
                  >
                    I understand
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    className="w-fit text-xs"
                    onClick={handleRemindLater}
                  >
                    Remind me later
                  </Button>
                </motion.div>
              )}
            </div>

            {actions && (
              <button
                onClick={handleDismiss}
                className="absolute top-2 right-4 text-gray-500 hover:text-gray-700 focus:outline-hidden"
              >
                ×
              </button>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default InfoCard;
